import { Link } from 'react-router-dom';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';
import StaticPageShell, { StaticCtaCard, StaticSection } from '../components/StaticPageShell';

const Shipping = () => {
    const { t } = useTranslation();
    const tr = (key: string, fallback: string) => translateWithFallback(t, key, fallback);

    const sections = [
        {
            icon: '🇧🇪',
            titleKey: 'page.shipping.belgium.title',
            titleFb: 'Shipping within Belgium',
            textKey: 'page.shipping.belgium.text',
            textFb:
                'Sellers choose their preferred carrier — typically bpost, DPD, or Mondial Relay. Delivery usually takes 1–3 business days for domestic parcels. Tracking is provided when available.',
        },
        {
            icon: '🇪🇺',
            titleKey: 'page.shipping.eu.title',
            titleFb: 'Shipping to the EU',
            textKey: 'page.shipping.eu.text',
            textFb:
                'Some sellers ship to the Netherlands, France, Germany, and Luxembourg. International shipping costs and estimated delivery times are listed on each product page before checkout.',
        },
        {
            icon: '📍',
            titleKey: 'page.shipping.pickup.title',
            titleFb: 'Local pickup',
            textKey: 'page.shipping.pickup.text',
            // Chat messaging deferred — avoid “via chat” until feature ships
            textFb:
                'Some listings show an “Open to local pickup” badge and the city. Payment is still on Petito first. Meeting details come after pay. If you are unsure, choose Bpost.',
        },
        {
            icon: '📦',
            titleKey: 'page.shipping.packaging.title',
            titleFb: 'Packaging',
            textKey: 'page.shipping.packaging.text',
            textFb:
                'Sellers should pack items securely to prevent damage in transit. Fragile items (strollers, monitors) should be wrapped and boxed appropriately.',
        },
        {
            icon: '↩️',
            titleKey: 'page.shipping.returns.title',
            titleFb: 'Returns & disputes',
            textKey: 'page.shipping.returns.text',
            textFb:
                'Petito is a C2C marketplace. Returns are negotiated between buyer and seller unless an item was significantly misdescribed or unsafe. If you receive a damaged or incorrect item, contact the seller within 48 hours and open a helpdesk ticket if needed.',
        },
        {
            icon: '⚖️',
            titleKey: 'page.shipping.rights.title',
            titleFb: 'Your consumer rights',
            textKey: 'page.shipping.rights.text',
            textFb:
                'When buying from a professional seller (occasional traders), EU consumer protection may apply including a 14-day withdrawal right for distance sales. Private peer-to-peer sales between individuals are generally excluded from this right.',
        },
    ];

    return (
        <StaticPageShell
            eyebrow={tr('page.shipping.eyebrow', 'Orders')}
            title={tr('page.shipping.title', 'Shipping & Returns')}
            subtitle={tr('page.shipping.updated', 'Last updated: June 2026')}
            maxWidth="3xl"
        >
            <div className="grid gap-4 sm:grid-cols-2 sm:gap-5">
                {sections.map((s) => (
                    <StaticSection key={s.titleKey} icon={s.icon} title={tr(s.titleKey, s.titleFb)}>
                        <p>{tr(s.textKey, s.textFb)}</p>
                    </StaticSection>
                ))}
            </div>

            <StaticCtaCard>
                <p className="mb-3">
                    {tr(
                        'page.shipping.help.cta',
                        'Need help with a delivery or return?'
                    )}
                </p>
                <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
                    <Link
                        to="/helpdesk"
                        className="inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-[var(--accent-color)] px-6 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 sm:w-auto"
                    >
                        {tr('page.faq.contact.link', 'Contact our helpdesk')}
                    </Link>
                    <Link
                        to="/faq"
                        className="inline-flex min-h-11 w-full items-center justify-center rounded-xl border border-[var(--accent-color)] bg-white/60 px-6 py-2.5 text-sm font-semibold text-[var(--text-color)] transition hover:bg-white/90 sm:w-auto"
                    >
                        {tr('page.faq.title', 'Frequently Asked Questions')}
                    </Link>
                </div>
            </StaticCtaCard>
        </StaticPageShell>
    );
};

export default Shipping;
